<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2" style="background-color: #e0e0e0;">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">
                        Galeria de Imagens
                    </v-toolbar-title>
                    <v-spacer/>
                    <v-btn color="primary" small @click="openCreateFolderDialog(null)">
                        <v-icon left>mdi-folder-plus</v-icon>
                        Nova Pasta
                    </v-btn>
                </v-toolbar>

                <v-row class="ma-1" style="height: calc(100vh - 140px);">
                    <!-- Folder tree -->
                    <v-col cols="3" class="pa-0">
                        <v-card elevation="2" style="height: 100%; overflow-y: auto;">
                            <v-card-title class="subtitle-2 py-2 px-3">
                                <v-icon small class="mr-1">mdi-folder-multiple</v-icon>
                                Pastas
                            </v-card-title>
                            <v-divider/>
                            <v-card-text class="pa-1">
                                <v-treeview
                                    :items="folderTree"
                                    item-key="id"
                                    item-text="name"
                                    activatable
                                    :active.sync="activeFolderIds"
                                    open-all
                                    dense
                                >
                                    <template v-slot:label="{ item }">
                                        <span class="body-2">{{ item.name }}</span>
                                    </template>
                                    <template v-slot:append="{ item }">
                                        <v-btn
                                            icon
                                            x-small
                                            class="mr-1"
                                            title="Nova sub-pasta"
                                            @click.stop="openCreateFolderDialog(item)"
                                        >
                                            <v-icon x-small>mdi-folder-plus-outline</v-icon>
                                        </v-btn>
                                        <v-btn
                                            icon
                                            x-small
                                            class="mr-1"
                                            title="Renomear"
                                            @click.stop="openRenameFolderDialog(item)"
                                        >
                                            <v-icon x-small>mdi-pencil</v-icon>
                                        </v-btn>
                                        <v-btn
                                            icon
                                            x-small
                                            title="Excluir pasta"
                                            @click.stop="confirmDeleteFolder(item)"
                                        >
                                            <v-icon x-small color="error">mdi-delete</v-icon>
                                        </v-btn>
                                    </template>
                                </v-treeview>

                                <div v-if="!folders.length" class="text-center caption grey--text pa-4">
                                    Nenhuma pasta criada.<br>Clique em "Nova Pasta" para começar.
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Image grid -->
                    <v-col cols="9" class="pa-0 pl-2">
                        <v-card elevation="2" style="height: 100%; overflow-y: auto;">
                            <v-card-title class="subtitle-2 py-2 px-3">
                                <v-icon small class="mr-1">mdi-image-multiple</v-icon>
                                <span v-if="activeFolder">
                                    Imagens — <span class="primary--text">{{ activeFolder.path }}</span>
                                </span>
                                <span v-else class="grey--text">Selecione uma pasta</span>
                                <v-spacer/>
                                <v-btn
                                    v-if="activeFolder"
                                    color="primary"
                                    small
                                    @click="openUploadDialog"
                                >
                                    <v-icon left>mdi-upload</v-icon>
                                    Upload
                                </v-btn>
                            </v-card-title>
                            <v-divider/>

                            <v-card-text>
                                <div v-if="!activeFolder" class="text-center grey--text pa-8">
                                    <v-icon large color="grey lighten-1">mdi-arrow-left</v-icon>
                                    <p class="mt-2">Selecione uma pasta na árvore ao lado.</p>
                                </div>

                                <div v-else-if="loadingImages" class="text-center pa-8">
                                    <v-progress-circular indeterminate color="primary"/>
                                </div>

                                <div v-else-if="!images.length" class="text-center grey--text pa-8">
                                    <v-icon large color="grey lighten-1">mdi-image-off</v-icon>
                                    <p class="mt-2">Nenhuma imagem nesta pasta.</p>
                                </div>

                                <v-row v-else dense>
                                    <v-col
                                        v-for="image in images"
                                        :key="image.id"
                                        cols="6" sm="4" md="3" lg="2"
                                    >
                                        <v-card class="image-card" elevation="1">
                                            <v-img
                                                :src="getImageUrl(image.storage_path)"
                                                aspect-ratio="1"
                                                contain
                                                class="grey lighten-3"
                                            >
                                                <template v-slot:placeholder>
                                                    <v-row align="center" justify="center" style="height: 100%">
                                                        <v-progress-circular indeterminate color="grey"/>
                                                    </v-row>
                                                </template>
                                            </v-img>

                                            <v-card-text class="pa-1">
                                                <div class="caption text-truncate" :title="image.name">
                                                    {{ image.name }}
                                                </div>
                                                <div
                                                    class="caption grey--text text-truncate"
                                                    style="font-size: 10px !important;"
                                                    :title="image.key"
                                                >
                                                    <v-icon style="font-size: 10px;">mdi-key</v-icon>
                                                    {{ image.key }}
                                                </div>
                                            </v-card-text>

                                            <v-card-actions class="pa-1 pt-0">
                                                <v-btn
                                                    x-small
                                                    text
                                                    color="primary"
                                                    @click="copyKey(image.key)"
                                                    title="Copiar key"
                                                >
                                                    <v-icon x-small>mdi-content-copy</v-icon>
                                                </v-btn>
                                                <v-spacer/>
                                                <v-btn
                                                    x-small
                                                    icon
                                                    color="error"
                                                    @click="confirmDeleteImage(image)"
                                                >
                                                    <v-icon x-small>mdi-delete</v-icon>
                                                </v-btn>
                                            </v-card-actions>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Dialog: Create / Rename Folder -->
                <v-dialog v-model="folderDialog.visible" max-width="400">
                    <v-card>
                        <v-card-title>{{ folderDialog.title }}</v-card-title>
                        <v-card-text>
                            <v-text-field
                                v-model="folderDialog.name"
                                label="Nome da pasta"
                                outlined
                                dense
                                autofocus
                                @keyup.enter="saveFolderDialog"
                            />
                            <div v-if="folderDialog.parentPath" class="caption grey--text">
                                Localização: <strong>{{ folderDialog.parentPath }}</strong>
                            </div>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn text @click="folderDialog.visible = false">Cancelar</v-btn>
                            <v-spacer/>
                            <v-btn
                                color="primary"
                                :loading="folderDialog.loading"
                                :disabled="!folderDialog.name.trim()"
                                @click="saveFolderDialog"
                            >
                                Salvar
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- Dialog: Upload Image -->
                <v-dialog v-model="uploadDialog.visible" max-width="550">
                    <v-card>
                        <v-card-title>
                            Upload de Imagens
                            <div class="caption grey--text ml-2">→ {{ uploadDialog.folderPath }}</div>
                        </v-card-title>
                        <v-card-text>
                            <div
                                class="upload-dropzone"
                                :class="{ 'drag-over': uploadDialog.isDragging }"
                                @click="$refs.uploadFileInput.click()"
                                @dragover.prevent="uploadDialog.isDragging = true"
                                @dragleave.prevent="uploadDialog.isDragging = false"
                                @drop.prevent="handleUploadDrop"
                            >
                                <div class="text-center grey--text pa-6">
                                    <v-icon large>mdi-image-plus</v-icon>
                                    <p class="caption mt-1">Clique ou arraste imagens aqui</p>
                                </div>
                            </div>
                            <input
                                ref="uploadFileInput"
                                type="file"
                                accept="image/*"
                                multiple
                                style="display:none"
                                @change="handleUploadFileSelect"
                            />

                            <div v-if="uploadDialog.items.length" class="mt-3" style="max-height: 300px; overflow-y: auto;">
                                <div
                                    v-for="(item, index) in uploadDialog.items"
                                    :key="index"
                                    class="d-flex align-center mb-2 pa-2"
                                    style="border: 1px solid #e0e0e0; border-radius: 8px;"
                                >
                                    <div class="mr-3" style="position: relative; width: 60px; height: 60px; flex-shrink: 0;">
                                        <v-img
                                            :src="item.previewUrl"
                                            width="60"
                                            height="60"
                                            contain
                                            class="grey lighten-3"
                                            style="border-radius: 4px;"
                                        />
                                        <div
                                            v-if="item.uploading"
                                            style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.75); border-radius: 4px;"
                                        >
                                            <v-progress-circular indeterminate size="20" color="primary"/>
                                        </div>
                                        <div
                                            v-else-if="item.error"
                                            style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.75); border-radius: 4px;"
                                        >
                                            <v-icon color="error">mdi-alert-circle</v-icon>
                                        </div>
                                    </div>
                                    <v-text-field
                                        v-model="item.name"
                                        label="Nome"
                                        outlined
                                        dense
                                        hide-details
                                        class="flex-grow-1"
                                        :disabled="item.uploading"
                                    />
                                    <v-btn icon small class="ml-2" :disabled="item.uploading" @click="removeUploadFile(index)">
                                        <v-icon small color="error">mdi-close</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn text @click="uploadDialog.visible = false">Cancelar</v-btn>
                            <v-spacer/>
                            <v-btn
                                color="primary"
                                :loading="uploadDialog.saving"
                                :disabled="uploadDialog.saving || uploadDialog.items.some(i => i.uploading) || !uploadDialog.items.some(i => i.tmpPath && i.name.trim())"
                                @click="submitUpload"
                            >
                                <v-icon left>mdi-upload</v-icon>
                                Enviar {{ uploadDialog.items.filter(i => i.tmpPath).length ? `(${uploadDialog.items.filter(i => i.tmpPath).length})` : '' }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';

export default {
    name: 'GaleriaImagens',

    components: { BaseContainer },

    data: () => ({
        folders: [],
        folderTree: [],
        activeFolderIds: [],
        images: [],
        loadingImages: false,

        folderDialog: {
            visible: false,
            title: '',
            name: '',
            parentId: null,
            parentPath: '',
            editingFolder: null,
            loading: false,
        },

        uploadDialog: {
            visible: false,
            items: [],   // { file, previewUrl, name, tmpPath, uploading, error }
            folderPath: '',
            isDragging: false,
            saving: false,
        },
    }),

    computed: {
        activeFolder() {
            if (!this.activeFolderIds.length) return null;
            return this.folders.find(f => f.id === this.activeFolderIds[0]) || null;
        },
    },

    watch: {
        activeFolderIds(ids) {
            if (ids.length) this.loadImages(ids[0]);
            else this.images = [];
        },
    },

    mounted() {
        this.loadFolders();
    },

    methods: {
        // ── Folders ────────────────────────────────────────────────────────────
        loadFolders() {
            this.$http.get('gallery/folders').then(resp => {
                this.folders = resp.data.data || [];
                this.folderTree = this.buildTree(this.folders, null);
            }).catch(() => {
                this.notify('Erro ao carregar pastas', 'warning');
            });
        },

        buildTree(flat, parentId) {
            return flat
                .filter(f => (f.parent_id ?? null) === parentId)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(f => ({
                    id: f.id,
                    name: f.name,
                    path: f.path,
                    children: this.buildTree(flat, f.id),
                }));
        },

        openCreateFolderDialog(parentItem) {
            this.folderDialog = {
                visible: true,
                title: parentItem ? `Nova pasta em "${parentItem.name}"` : 'Nova Pasta Raiz',
                name: '',
                parentId: parentItem ? parentItem.id : null,
                parentPath: parentItem ? parentItem.path : '',
                editingFolder: null,
                loading: false,
            };
        },

        openRenameFolderDialog(item) {
            const folder = this.folders.find(f => f.id === item.id);
            this.folderDialog = {
                visible: true,
                title: 'Renomear Pasta',
                name: folder.name,
                parentId: folder.parent_id,
                parentPath: folder.path,
                editingFolder: folder,
                loading: false,
            };
        },

        saveFolderDialog() {
            if (!this.folderDialog.name.trim()) return;
            this.folderDialog.loading = true;

            const isEdit = !!this.folderDialog.editingFolder;
            const req = isEdit
                ? this.$http.put(`gallery/folders/${this.folderDialog.editingFolder.id}`, { name: this.folderDialog.name })
                : this.$http.post('gallery/folders', { name: this.folderDialog.name, parent_id: this.folderDialog.parentId });

            req.then(() => {
                this.folderDialog.visible = false;
                this.notify(isEdit ? 'Pasta renomeada!' : 'Pasta criada!');
                this.loadFolders();
            }).catch(() => {
                this.notify('Erro ao salvar pasta', 'warning');
            }).finally(() => (this.folderDialog.loading = false));
        },

        confirmDeleteFolder(item) {
            if (!confirm(`Excluir a pasta "${item.name}" e todas as imagens dentro dela?`)) return;
            this.$http.delete(`gallery/folders/${item.id}`).then(() => {
                this.notify('Pasta excluída!');
                if (this.activeFolderIds[0] === item.id) {
                    this.activeFolderIds = [];
                    this.images = [];
                }
                this.loadFolders();
            }).catch(() => {
                this.notify('Erro ao excluir pasta', 'warning');
            });
        },

        // ── Images ─────────────────────────────────────────────────────────────
        loadImages(folderId) {
            this.loadingImages = true;
            this.images = [];
            this.$http.get('gallery/images', { params: { folder_id: folderId } }).then(resp => {
                this.images = resp.data.data || [];
            }).catch(() => {
                this.notify('Erro ao carregar imagens', 'warning');
            }).finally(() => (this.loadingImages = false));
        },

        getImageUrl(storagePath) {
            return `${this.$urlAPI}/storage/${storagePath}`;
        },

        openUploadDialog() {
            this.uploadDialog = {
                visible: true,
                items: [],
                folderPath: this.activeFolder?.path || '',
                isDragging: false,
                saving: false,
            };
        },

        handleUploadFileSelect(e) {
            const files = Array.from(e.target.files);
            this.addUploadFiles(files);
            e.target.value = '';
        },

        handleUploadDrop(e) {
            this.uploadDialog.isDragging = false;
            const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
            this.addUploadFiles(files);
        },

        addUploadFiles(files) {
            files.forEach(file => {
                const item = {
                    file,
                    previewUrl: null,
                    name: file.name.replace(/\.[^/.]+$/, ''),
                    tmpPath: null,
                    uploading: true,
                    error: false,
                };

                const reader = new FileReader();
                reader.onload = ev => (item.previewUrl = ev.target.result);
                reader.readAsDataURL(file);

                this.uploadDialog.items.push(item);

                // Upload imediatamente para tmp (1 request por arquivo)
                const formData = new FormData();
                formData.append('file', file);
                this.$http.post('files/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                }).then(resp => {
                    item.tmpPath = resp.data.path;
                    item.uploading = false;
                }).catch(() => {
                    item.error = true;
                    item.uploading = false;
                });
            });
        },

        removeUploadFile(index) {
            this.uploadDialog.items.splice(index, 1);
        },

        submitUpload() {
            const readyItems = this.uploadDialog.items.filter(i => i.tmpPath && i.name.trim());
            if (!readyItems.length) return;

            this.uploadDialog.saving = true;

            this.$http.post('gallery/images', {
                folder_id: this.activeFolder.id,
                images: readyItems.map(i => ({ tmp_path: i.tmpPath, name: i.name.trim() })),
            }).then(() => {
                const count = readyItems.length;
                this.uploadDialog.visible = false;
                this.notify(count === 1 ? 'Imagem enviada!' : `${count} imagens enviadas!`);
                this.loadImages(this.activeFolder.id);
            }).catch(() => {
                this.notify('Erro ao salvar imagens', 'warning');
            }).finally(() => (this.uploadDialog.saving = false));
        },

        confirmDeleteImage(image) {
            if (!confirm(`Excluir a imagem "${image.name}"?`)) return;
            this.$http.delete(`gallery/images/${image.id}`).then(() => {
                this.notify('Imagem excluída!');
                this.images = this.images.filter(i => i.id !== image.id);
            }).catch(() => {
                this.notify('Erro ao excluir imagem', 'warning');
            });
        },

        copyKey(key) {
            navigator.clipboard.writeText(key).then(() => {
                this.notify(`Key copiada: ${key}`);
            });
        },
    },
};
</script>

<style scoped>
.image-card {
    transition: box-shadow 0.2s;
}
.image-card:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.2) !important;
}
.upload-dropzone {
    border: 2px dashed #bbb;
    border-radius: 8px;
    cursor: pointer;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s;
}
.upload-dropzone:hover,
.upload-dropzone.drag-over {
    border-color: #1976d2;
}
</style>
